import {useRouter} from 'next/router';

import {useCurrentSection} from 'Application/useCases/useCurrentSection';

import {getDownloadAttribute, getHrefForLinkType, getRelAttributes} from 'Application/utils/linkFieldUtils';

import type {LinkField} from 'types/payload-types';

/**
 * A custom React hook that encapsulates logic for handling navigation link behavior, including
 * determining active states, scroll behavior for anchor links, download attributes, and target/rel flags.
 * This hook supports different link types such as internal routes, external URLs, file downloads, and anchor-based
 * scrolling. It automatically determines whether a link is currently active by comparing the current route to the
 * link's href. For anchor links, it ensures the corresponding element is scrolled into view after route changes.
 *
 * @param link          The navigation link object, containing label, linkType, href, blockId, and other metadata.
 * @param link.label    The displayed label for the navigation item.
 * @param link.linkType The type of link, such as 'internal', 'external', 'file', or 'anchor'.
 * @param onClick       An optional callback function to be executed when the navigation item is clicked.
 * @returns An object with computed values for the link including href, active state, download flag, scroll behavior, rel, and target attributes.
 *
 * @example
 * ```tsx
 * const linkProps = useNavItem(link);
 *
 * return (
 *   <a
 *     href={linkProps.href}
 *     target={linkProps.target}
 *     rel={linkProps.rel}
 *     download={linkProps.isDownload}
 *     data-active={linkProps.isActive}
 *   >
 *     {linkProps.label}
 *   </a>
 * );
 * ```
 */
export const useNavItem = (link: LinkField, onClick?: () => void) => {
    const {currentSection} = useCurrentSection();
    const router = useRouter();
    const {label, linkType} = link;
    const href = getHrefForLinkType(link);
    const isDownload = getDownloadAttribute(link);
    const rel = getRelAttributes(link);
    const target = linkType === 'external' || linkType === 'file' ? '_blank' : undefined;

    /**
     * Handles click events for the navigation item. This function executes the optional onClick callback
     * when provided, allowing for custom behavior such as closing mobile menus or triggering analytics events.
     * The function serves as a bridge between the navigation item's click event and any additional actions
     * that need to be performed beyond the standard link navigation.
     *
     * @example
     * ```tsx
     * const handleClick = () => {
     *     if (onClick) {
     *         onClick();
     *     }
     * };
     * ```
     */
    const handleClick = () => {
        if (onClick) {
            onClick();
        }
    };

    return {
        handleClick,
        href,
        isActive: currentSection === link.blockId,
        isDownload,
        label,
        rel,
        router,
        target
    };
};